import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  List,
  ListItem,
  ListItemText,
  Divider,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Drawer,
  List as MuiList,
  ListItemButton,
  ListItemIcon,
  Toolbar,
  AppBar,
  CssBaseline,
  IconButton,
  useTheme,
  Card,
  CardContent,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import SettingsIcon from "@mui/icons-material/Settings";
import BusinessIcon from "@mui/icons-material/Business";
import PeopleIcon from "@mui/icons-material/People";
import ListAltIcon from "@mui/icons-material/ListAlt";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import systemAPI from "../../utils/systemAPI";
import ManagerManagement from "./ManagerManagement";

const drawerWidth = 240;
const navItems = [
  { label: "System Config", icon: <SettingsIcon />, tab: 0 },
  { label: "Departments", icon: <BusinessIcon />, tab: 1 },
  { label: "Admin Users", icon: <PeopleIcon />, tab: 2 },
  { label: "Manager Users", icon: <PeopleIcon />, tab: 4 },
  { label: "System Logs", icon: <ListAltIcon />, tab: 3 },
];

export default function DeveloperMasterPanel() {
  const [tabValue, setTabValue] = useState(0);
  const [systemConfig, setSystemConfig] = useState({});
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newDepartment, setNewDepartment] = useState({
    name: "",
    code: "",
    description: "",
  });
  const [successMessage, setSuccessMessage] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [search, setSearch] = useState("");
  const theme = useTheme();

  // System logs state
  const [systemLogs, setSystemLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logFilter, setLogFilter] = useState("all");

  // Admin creation state
  const [adminForm, setAdminForm] = useState({
    name: "",
    email: "",
    password: "",
    department: "",
  });
  const [adminSuccess, setAdminSuccess] = useState("");
  const [adminError, setAdminError] = useState("");

  // Fetch system logs function
  const fetchSystemLogs = useCallback(async () => {
    try {
      setLogsLoading(true);
      const response = await systemAPI.getSystemLogs(logFilter);
      setSystemLogs(response.data || []);
    } catch (err) {
      console.error("Error loading system logs:", err);
      setError("Failed to load system logs");
    } finally {
      setLogsLoading(false);
    }
  }, [logFilter]);

  // Load system logs when logs tab is selected
  useEffect(() => {
    if (tabValue === 3) {
      fetchSystemLogs();
    }
  }, [tabValue, fetchSystemLogs]);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);
        const configResponse = await systemAPI.getSystemConfig();
        setSystemConfig(configResponse.data);
        const departmentsResponse = await systemAPI.getDepartments();
        setDepartments(departmentsResponse.data);
      } catch (err) {
        console.error("Error loading developer data:", err);
        setError(
          "Failed to load developer settings. Please ensure you have Developer Master permissions."
        );
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const showSuccess = (message) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(""), 3000);
  };

  const handleConfigUpdate = async (field, value) => {
    try {
      setError(null);
      const updatedConfig = { ...systemConfig, [field]: value };
      await systemAPI.updateSystemConfig({ [field]: value });
      setSystemConfig(updatedConfig);
      showSuccess(`System configuration updated successfully`);
    } catch (err) {
      console.error("Error updating config:", err);
      setError("Failed to update system configuration");
    }
  };

  const handleDepartmentChange = (field, value) => {
    setNewDepartment({ ...newDepartment, [field]: value });
  };

  const handleCreateDepartment = async () => {
    try {
      setError(null);
      const response = await systemAPI.createDepartment(newDepartment);
      setDepartments([...departments, response.data]);
      setNewDepartment({ name: "", code: "", description: "" });
      showSuccess("Department created successfully");
    } catch (err) {
      console.error("Error creating department:", err);
      setError("Failed to create department");
    }
  };

  const handleDeleteDepartment = async (id) => {
    if (
      window.confirm(
        "Are you sure you want to delete this department? This action cannot be undone."
      )
    ) {
      try {
        setError(null);
        await systemAPI.deleteDepartment(id);
        setDepartments(departments.filter((dept) => dept.id !== id));
        showSuccess("Department deleted successfully");
      } catch (err) {
        console.error("Error deleting department:", err);
        setError("Failed to delete department");
      }
    }
  };

  const handleClearCache = async () => {
    try {
      setError(null);
      await systemAPI.clearSystemCache();
      showSuccess("System cache cleared successfully");
    } catch (err) {
      console.error("Error clearing cache:", err);
      setError("Failed to clear system cache");
    }
  };

  // Handle log filtering
  const handleFilterLogs = (filter) => {
    setLogFilter(filter);
    fetchSystemLogs();
  };

  // Department search/filter
  const filteredDepartments = departments.filter(
    (dept) =>
      dept.name.toLowerCase().includes(search.toLowerCase()) ||
      (dept.code || "").toLowerCase().includes(search.toLowerCase()) ||
      (dept.description || "").toLowerCase().includes(search.toLowerCase())
  );

  // Sidebar Drawer
  const drawer = (
    <div>
      <Toolbar />
      <Divider />
      <MuiList>
        {navItems.map((item) => (
          <ListItem key={item.label} disablePadding>
            <ListItemButton
              selected={tabValue === item.tab}
              onClick={() => setTabValue(item.tab)}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.label} />
            </ListItemButton>
          </ListItem>
        ))}
      </MuiList>
    </div>
  );

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "400px",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Simplified admin creation handler
  const handleAdminInput = (field, value) => {
    setAdminForm({ ...adminForm, [field]: value });
  };

  const handleCreateAdmin = async () => {
    setAdminError("");
    setAdminSuccess("");
    try {
      // Use backend API to create admin (do NOT use Firebase Auth client SDK)
      const response = await systemAPI.createAdmin({
        name: adminForm.name,
        email: adminForm.email,
        password: adminForm.password, // Or let backend generate temp password
        department: adminForm.department,
      });
      setAdminSuccess(
        response.data?.message || "Admin user created successfully!"
      );
      setAdminForm({ name: "", email: "", password: "", department: "" });
    } catch (err) {
      setAdminError(
        err.response?.data?.message ||
          err.message ||
          "Failed to create admin user"
      );
    }
  };

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <AppBar position="fixed" sx={{ zIndex: theme.zIndex.drawer + 1 }}>
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: "none" } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div">
            Developer Master Panel
          </Typography>
        </Toolbar>
      </AppBar>
      <Drawer
        variant="permanent"
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          [`& .MuiDrawer-paper`]: {
            width: drawerWidth,
            boxSizing: "border-box",
          },
          display: { xs: "none", sm: "block" },
        }}
        open
      >
        {drawer}
      </Drawer>
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: "block", sm: "none" },
          [`& .MuiDrawer-paper`]: {
            width: drawerWidth,
            boxSizing: "border-box",
          },
        }}
      >
        {drawer}
      </Drawer>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
        }}
      >
        <Toolbar />
        <Typography variant="h4" component="h1" gutterBottom>
          Developer Master Panel
        </Typography>
        <Typography variant="subtitle1" gutterBottom>
          System-wide configuration and management
        </Typography>
        {error && (
          <Alert severity="error" sx={{ my: 2 }}>
            {error}
          </Alert>
        )}
        {successMessage && (
          <Alert severity="success" sx={{ my: 2 }}>
            {successMessage}
          </Alert>
        )}
        {/* System Config Tab */}
        {tabValue === 0 && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6">System Configuration</Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={systemConfig.maintenanceMode || false}
                    onChange={(e) =>
                      handleConfigUpdate("maintenanceMode", e.target.checked)
                    }
                  />
                }
                label="Maintenance Mode"
              />
              <TextField
                margin="normal"
                fullWidth
                label="API Rate Limit"
                type="number"
                value={systemConfig.apiRateLimit || ""}
                onChange={(e) =>
                  handleConfigUpdate("apiRateLimit", parseInt(e.target.value))
                }
              />
              <TextField
                margin="normal"
                fullWidth
                label="Application Version"
                value={systemConfig.version || ""}
                onChange={(e) => handleConfigUpdate("version", e.target.value)}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={systemConfig.emailNotifications || false}
                    onChange={(e) =>
                      handleConfigUpdate("emailNotifications", e.target.checked)
                    }
                  />
                }
                label="Email Notifications"
              />
              <Box sx={{ mt: 2 }}>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={handleClearCache}
                >
                  Clear System Cache
                </Button>
              </Box>
            </CardContent>
          </Card>
        )}
        {/* Departments Tab */}
        {tabValue === 1 && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Department Management
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                To manage departments (create, edit, delete), please use the
                centralized Department Management page.
              </Typography>
              <Button
                variant="contained"
                color="primary"
                href="/department-management"
              >
                Go to Department Management
              </Button>
            </CardContent>
          </Card>
        )}
        {/* Admin Users Tab */}
        {tabValue === 2 && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Admin User Management
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                To manage admin users (create, edit, assign departments), please
                use the centralized Admin Management page.
              </Typography>
              <Button
                variant="contained"
                color="primary"
                href="/admin-management"
              >
                Go to Admin Management
              </Button>
            </CardContent>
          </Card>
        )}
        {/* System Logs Tab */}
        {tabValue === 3 && (
          <Card>
            <CardContent>
              <Typography variant="h6">System Logs</Typography>
              <Box
                sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}
              >
                <Box>
                  <Button
                    onClick={() => handleFilterLogs("all")}
                    variant={logFilter === "all" ? "contained" : "outlined"}
                    sx={{ mr: 1 }}
                    size="small"
                  >
                    All
                  </Button>
                  <Button
                    onClick={() => handleFilterLogs("error")}
                    variant={logFilter === "error" ? "contained" : "outlined"}
                    sx={{ mr: 1 }}
                    size="small"
                    color="error"
                  >
                    Errors
                  </Button>
                  <Button
                    onClick={() => handleFilterLogs("warning")}
                    variant={logFilter === "warning" ? "contained" : "outlined"}
                    sx={{ mr: 1 }}
                    size="small"
                    color="warning"
                  >
                    Warnings
                  </Button>
                  <Button
                    onClick={() => handleFilterLogs("info")}
                    variant={logFilter === "info" ? "contained" : "outlined"}
                    size="small"
                    color="info"
                  >
                    Info
                  </Button>
                </Box>
                <Button
                  onClick={fetchSystemLogs}
                  startIcon={<RefreshIcon />}
                  variant="outlined"
                  size="small"
                >
                  Refresh
                </Button>
              </Box>

              {logsLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                  <CircularProgress size={30} />
                </Box>
              ) : systemLogs.length === 0 ? (
                <Alert severity="info">
                  No logs found matching the current filter.
                </Alert>
              ) : (
                <List
                  sx={{
                    bgcolor: "background.paper",
                    maxHeight: 400,
                    overflow: "auto",
                  }}
                >
                  {systemLogs.map((log, index) => (
                    <React.Fragment key={log.id || index}>
                      <ListItem
                        alignItems="flex-start"
                        sx={{
                          backgroundColor:
                            log.level === "error"
                              ? "error.light"
                              : log.level === "warning"
                              ? "warning.light"
                              : "transparent",
                        }}
                      >
                        <ListItemText
                          primary={
                            <Typography
                              component="span"
                              variant="body1"
                              fontWeight="bold"
                            >
                              {log.message}
                            </Typography>
                          }
                          secondary={
                            <>
                              <Typography
                                component="span"
                                variant="body2"
                                color="text.primary"
                                sx={{ display: "block" }}
                              >
                                {log.timestamp
                                  ? new Date(log.timestamp).toLocaleString()
                                  : "No timestamp"}
                              </Typography>
                              <Typography component="span" variant="body2">
                                User: {log.user || "System"} | Level:{" "}
                                {log.level || "info"} | Source:{" "}
                                {log.source || "Unknown"}
                              </Typography>
                            </>
                          }
                        />
                      </ListItem>
                      <Divider component="li" />
                    </React.Fragment>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        )}
        {tabValue === 4 && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <ManagerManagement />
            </CardContent>
          </Card>
        )}
      </Box>
    </Box>
  );
}
